import { useState } from 'react';
import { findSubgenreMatch, normalizeSubgenreName } from '../../lib/genreTaxonomy';

/**
 * GenreSubgenrePicker — Control A, from `AddBook_Reshape`
 * (docs/genre-subgenre-build-spec.md, Part 3B / PR 3).
 *
 * "Is this person describing a BOOK, or describing THEMSELVES?" Control A is
 * for the first case: attaching genre/subgenre to one specific book — a
 * submission, a manual tag, a 5-star read. Multi-select genre chips, one
 * search-and-pick section per selected genre, write-in always offered last,
 * and a "did you mean X?" prompt when the typed name is close to (or exactly)
 * an existing canon or cross-genre subgenre. No reader-vs-author mode: this
 * is a straight port of AddBook.jsx's GenreSubgenrePicker/SubgenreSection,
 * with the desktop/mobile ChipD/ChipM split replaced by one injected `Chip`
 * prop — every caller (author or reader) brings its own chip look, the same
 * way GenreSubgenreFilter (Control B) does.
 *
 * Taste never mints vocabulary lightly here either, but unlike Control B a
 * write-in IS offered — Part 5's "never hidden" rule — because this control
 * describes a specific book, and a book with no matching subgenre still
 * needs to be findable under something until a curator reviews it.
 *
 * State ownership: the caller holds selectedGenreIds/selectedSubgenreIds/
 * subgenreNamesById (a write-in's id isn't in canonSubgenresForGenre until
 * it's reviewed, so its name has to be tracked alongside the id) — same
 * shape AddBook.jsx already uses, so a book's genre/subgenre picks look
 * identical whether they were made on the author side or a reader surface.
 *
 * @param {Object} props
 * @param {React.ComponentType<{on:boolean, onClick:()=>void, children:*}>} props.Chip
 * @param {Array<{id:number,name:string}>} props.genres  ordered by sort_order
 * @param {(genreId:number) => Array<{id:number,name:string,genre_id:number}>} props.canonSubgenresForGenre
 * @param {Object} props.matchIndex  from useGenreTaxonomy()
 * @param {(genreId:number, name:string) => Promise<{data:{id,name,genre_id}}|{error:string}>} props.upsertPendingSubgenre
 * @param {number[]} props.selectedGenreIds
 * @param {(updater: number[] | ((prev:number[])=>number[])) => void} props.setSelectedGenreIds
 * @param {number[]} props.selectedSubgenreIds
 * @param {(updater: number[] | ((prev:number[])=>number[])) => void} props.setSelectedSubgenreIds
 * @param {Object<number,{name:string,genreId:number}>} props.subgenreNamesById  write-ins + any subgenre this caller needs a name for without a fresh lookup
 * @param {(updater: Object | ((prev:Object)=>Object)) => void} props.setSubgenreNamesById
 * @param {boolean} [props.loading]
 * @param {string} [props.error]
 */
export default function GenreSubgenrePicker({
  Chip,
  genres,
  canonSubgenresForGenre,
  matchIndex,
  upsertPendingSubgenre,
  selectedGenreIds,
  setSelectedGenreIds,
  selectedSubgenreIds,
  setSelectedSubgenreIds,
  subgenreNamesById,
  setSubgenreNamesById,
  loading = false,
  error = '',
}) {
  function toggleGenre(id) {
    if (selectedGenreIds.includes(id)) {
      // Unchecking a genre also drops its subgenres. A subgenre's parent is
      // DERIVED from the subgenre row (spec Part 2: "never stored twice"),
      // so holding a subgenre while its genre is unchecked has no coherent
      // meaning in this model.
      const toDrop = new Set(canonSubgenresForGenre(id).map(s => s.id));
      for (const [sid, info] of Object.entries(subgenreNamesById)) {
        if (info.genreId === id) toDrop.add(Number(sid));
      }
      setSelectedSubgenreIds(prev => prev.filter(sid => !toDrop.has(sid)));
      setSelectedGenreIds(prev => prev.filter(g => g !== id));
    } else {
      setSelectedGenreIds(prev => [...prev, id]);
    }
  }

  if (error) {
    return (
      <p role="status" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', margin: '10px 0 0' }}>
        Couldn't load the genre list right now, {error}
      </p>
    );
  }

  if (loading) {
    return (
      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--neutral-400)', fontStyle: 'italic', margin: '10px 0 0' }}>
        Loading genres…
      </p>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
        {genres.map(g => (
          <Chip key={g.id} on={selectedGenreIds.includes(g.id)} onClick={() => toggleGenre(g.id)}>{g.name}</Chip>
        ))}
      </div>

      {selectedGenreIds.map(genreId => {
        const genre = genres.find(g => g.id === genreId);
        if (!genre) return null; // taxonomy still loading, or a stale id round-tripped from a draft
        return (
          <SubgenreSection
            key={genreId}
            Chip={Chip}
            genre={genre}
            canonOptions={canonSubgenresForGenre(genreId)}
            matchIndex={matchIndex}
            upsertPendingSubgenre={upsertPendingSubgenre}
            selectedSubgenreIds={selectedSubgenreIds}
            setSelectedSubgenreIds={setSelectedSubgenreIds}
            subgenreNamesById={subgenreNamesById}
            setSubgenreNamesById={setSubgenreNamesById}
            setSelectedGenreIds={setSelectedGenreIds}
          />
        );
      })}
    </div>
  );
}

// Chip has to be passed down: it is a prop of GenreSubgenrePicker, not a
// module-level component, so this section cannot see it otherwise. Leaving it
// out threw "Chip is not defined" the moment a reader picked any genre, on
// every reader surface that uses this picker.
function SubgenreSection({
  Chip, genre, canonOptions, matchIndex, upsertPendingSubgenre,
  selectedSubgenreIds, setSelectedSubgenreIds, subgenreNamesById, setSubgenreNamesById, setSelectedGenreIds,
}) {
  const [search, setSearch] = useState('');
  const [prompt, setPrompt] = useState(null); // { typed, match } | { typed, error } | null
  const [busy, setBusy] = useState(false);

  const selectedHere = canonOptions.filter(s => selectedSubgenreIds.includes(s.id));
  // Write-ins already picked under this genre -- not in canonOptions (that
  // list is canon-only, Part 5 decision 9: unpromoted, not offered to a
  // second author/reader), so they are tracked separately.
  //
  // attachSubgenre records EVERY pick in subgenreNamesById, listed ones too,
  // so a listed pick has to be left out here, or it shows twice: once above
  // and once again with a NEW badge.
  const canonIds = new Set(canonOptions.map(s => s.id));
  const pendingHere = Object.entries(subgenreNamesById)
    .filter(([id, info]) => info.genreId === genre.id && selectedSubgenreIds.includes(Number(id)) && !canonIds.has(Number(id)))
    .map(([id, info]) => ({ id: Number(id), name: info.name }));

  const searchNorm = search.trim().toLowerCase();
  const visibleMatches = (searchNorm
    ? canonOptions.filter(s => !selectedSubgenreIds.includes(s.id) && s.name.toLowerCase().includes(searchNorm))
    : canonOptions.filter(s => !selectedSubgenreIds.includes(s.id))
  ).slice(0, 40); // some genres run 30+ subgenres deep -- cap so a long list doesn't push the write-in row off-screen

  function attachSubgenre({ id, name, genre_id }) {
    setSelectedSubgenreIds(prev => (prev.includes(id) ? prev : [...prev, id]));
    setSubgenreNamesById(prev => ({ ...prev, [id]: { name, genreId: genre_id ?? genre.id } }));
    if (genre_id && genre_id !== genre.id) {
      // Cross-genre accept (the "Romantasy typed under Romance" case) -- the
      // matched subgenre belongs to a different genre, and its parent
      // cannot be re-pointed at whichever section it was typed into, so
      // that genre is added too rather than silently discarding the pick.
      setSelectedGenreIds(prev => (prev.includes(genre_id) ? prev : [...prev, genre_id]));
    }
    setSearch('');
    setPrompt(null);
  }

  function removeSubgenre(id) {
    setSelectedSubgenreIds(prev => prev.filter(x => x !== id));
  }

  async function createPending(typed) {
    const already = pendingHere.find(p => normalizeSubgenreName(p.name) === normalizeSubgenreName(typed));
    if (already) { setSearch(''); setPrompt(null); return; }
    setBusy(true);
    const { data, error } = await upsertPendingSubgenre(genre.id, typed);
    setBusy(false);
    if (error) { setPrompt({ typed, error }); return; }
    attachSubgenre({ id: data.id, name: data.name, genre_id: data.genre_id });
  }

  function handleWriteInClick() {
    const typed = search.trim();
    if (!typed) return;
    const found = findSubgenreMatch(typed, genre.id, matchIndex);

    if (!found) { createPending(typed); return; }
    if (found.kind === 'exact-same-genre') {
      attachSubgenre({ id: found.target.id, name: found.target.targetName, genre_id: found.target.genreId });
      return;
    }
    // 'exact-other-genre' or 'near' -- both need a yes/no, per Part 5's
    // three requirements (equal-weight buttons, say what agreeing buys
    // them, never a block).
    setPrompt({ typed, match: found });
  }

  const selectedChipButtonStyle = {
    padding: '7px 14px',
    borderRadius: 'var(--radius-md)',
    border: 'none',
    background: 'var(--brand-forest)',
    color: 'var(--brand-white)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'var(--font-ui)',
  };

  return (
    <div style={{ marginTop: 'var(--space-md)', paddingLeft: 'var(--space-md)', borderLeft: '2px solid var(--brand-sand)' }}>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--neutral-600)', marginBottom: 'var(--space-sm)' }}>
        {genre.name} subgenres <span style={{ color: 'var(--neutral-400)' }}>(optional)</span>
      </div>

      {(selectedHere.length > 0 || pendingHere.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
          {selectedHere.map(s => (
            <button key={s.id} onClick={() => removeSubgenre(s.id)} style={selectedChipButtonStyle}>
              {s.name} <span style={{ fontSize: 'var(--font-size-xs)', opacity: 0.7 }}>✕</span>
            </button>
          ))}
          {pendingHere.map(s => (
            <button key={s.id} onClick={() => removeSubgenre(s.id)} style={selectedChipButtonStyle} title="Not yet a listed subgenre -- fully attached here, just not offered to others until it's reviewed.">
              {s.name} <span style={{ fontSize: 10, opacity: 0.8, fontWeight: 700 }}>NEW</span> <span style={{ fontSize: 'var(--font-size-xs)', opacity: 0.7 }}>✕</span>
            </button>
          ))}
        </div>
      )}

      {prompt && !prompt.error && (
        <DidYouMeanPrompt prompt={prompt} busy={busy}
          onYes={() => attachSubgenre({ id: prompt.match.target.id, name: prompt.match.target.targetName, genre_id: prompt.match.target.genreId })}
          onNo={() => createPending(prompt.typed)}
        />
      )}

      {/* After a failed add the box and the list stay up, so "try again, or
          pick one from the list" is something the reader can actually do. */}
      {(!prompt || prompt.error) && (
        <>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); if (prompt?.error) setPrompt(null); }}
            onKeyDown={e => { if (e.key === 'Enter' && visibleMatches.length === 0) handleWriteInClick(); }}
            placeholder={`Search ${genre.name} subgenres…`}
            style={{ width: '100%', maxWidth: 360, padding: '8px 14px', fontSize: 'var(--font-size-base)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)', outline: 'none', background: 'var(--brand-paper)', color: 'var(--neutral-800)', fontFamily: 'var(--font-ui)' }}
          />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)' }}>
            {visibleMatches.map(s => (
              <Chip key={s.id} on={false} onClick={() => attachSubgenre({ id: s.id, name: s.name, genre_id: s.genre_id })}>{s.name}</Chip>
            ))}
            {search.trim() && (
              <button onClick={handleWriteInClick} disabled={busy}
                style={{ padding: '7px 14px', borderRadius: 'var(--radius-md)', border: '1px dashed var(--brand-sand)', background: 'var(--brand-white)', color: 'var(--neutral-600)', fontSize: 'var(--font-size-base)', fontFamily: 'var(--font-ui)', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.5 : 1 }}>
                + Add "{search.trim()}" as a new subgenre
              </button>
            )}
          </div>
        </>
      )}

      {prompt?.error && (
        <p role="status" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--feedback-warning-ink)', margin: '6px 0 0' }}>{prompt.error}</p>
      )}
    </div>
  );
}

/**
 * The "did you mean X?" screen. Part 5's three requirements, verbatim: both
 * buttons equally easy to press, say what agreeing buys them (being
 * findable), and never a block -- "No" saves the typed wording immediately,
 * same as "Yes" saves the match immediately.
 */
function DidYouMeanPrompt({ prompt, onYes, onNo, busy }) {
  const { typed, match } = prompt;
  const targetName = match.target.targetName;
  const isOtherGenre = match.kind === 'exact-other-genre';
  const buttonStyle = {
    flex: 1,
    padding: '9px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--brand-sand)',
    background: 'var(--brand-white)',
    color: 'var(--neutral-800)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    fontFamily: 'var(--font-ui)',
    cursor: busy ? 'not-allowed' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };

  return (
    <div style={{ padding: '10px 14px', background: 'var(--brand-cream)', border: '1px solid var(--brand-sand)', borderRadius: 'var(--radius-md)' }}>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--neutral-700)', lineHeight: 1.5, fontFamily: 'var(--font-ui)' }}>
        <strong>Did you mean {targetName}?</strong><br />
        You typed <strong>{typed}</strong>. {targetName} is already a subgenre readers browse
        {isOtherGenre ? <> under <strong>{match.target.genreName}</strong></> : null}, picking it means this shows up when they do{isOtherGenre ? <> (this also adds {match.target.genreName} as a genre)</> : null}.
      </p>
      {/* Equal weight, same size, same row -- Part 5 requirement 1. */}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'var(--space-sm)' }}>
        <button onClick={onYes} disabled={busy} style={buttonStyle}>Yes, use {targetName}</button>
        <button onClick={onNo} disabled={busy} style={buttonStyle}>No, keep {typed}</button>
      </div>
    </div>
  );
}
